import { useEffect, useMemo, useState } from "react";
import { GitMerge, Pencil, Plus, Trash2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Panel, PageTitle } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NEW = { discord_username: "", discord_id: "", activision_id: "", kills: 0, deaths: 0, wins: 0 };

const playerLabel = (player) => {
  if (!player) return "—";
  return player.activision_id || player.discord_username || player.discord_id || player.id;
};

export default function Giocatori() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [players, setPlayers] = useState([]);
  const [clans, setClans] = useState([]);
  const [duplicateReview, setDuplicateReview] = useState({
    tournament_id: null,
    tournament_name: null,
    excluded_player_ids: [],
  });
  const [excludingPlayerId, setExcludingPlayerId] = useState(null);
  const [searchActivision, setSearchActivision] = useState("");
  const [clanFilter, setClanFilter] = useState("");
  const [historyPlayerId, setHistoryPlayerId] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(NEW);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeSourceId, setMergeSourceId] = useState("");
  const [mergeTargetSearch, setMergeTargetSearch] = useState("");
  const [mergeSourceSearch, setMergeSourceSearch] = useState("");
  const [mergeTargetClan, setMergeTargetClan] = useState("");
  const [mergeSourceClan, setMergeSourceClan] = useState("");
  const [mergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeClanHistoryChoice, setMergeClanHistoryChoice] = useState("auto");
  const [mergeClanPeriods, setMergeClanPeriods] = useState({});
  const [mergeChoices, setMergeChoices] = useState({
    activision_id_choice: "target",
    discord_id_choice: "target",
    discord_username_choice: "target",
    avatar_choice: "target",
    kills_choice: "sum",
    deaths_choice: "sum",
    wins_choice: "sum",
    battle_royale_kd_choice: "auto",
    resurgence_kd_choice: "auto",
  });
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editForm, setEditForm] = useState({ ...NEW });
  const [editBusy, setEditBusy] = useState(false);

  const load = async () => {
    const [
      { data: playersData },
      { data: clansData },
      { data: reviewData },
    ] = await Promise.all([
      api.get("/players"),
      api.get("/clans"),
      api.get("/players/duplicate-review"),
    ]);
    setPlayers(playersData);
    setClans(clansData);
    setDuplicateReview(reviewData);
  };

  useEffect(() => { load(); }, []);

  const clanById = useMemo(
    () => Object.fromEntries(clans.map((clan) => [clan.id, clan])),
    [clans],
  );

  const duplicateInfo = useMemo(() => {
    const excludedIds = new Set(duplicateReview.excluded_player_ids || []);
    const candidates = players.filter((player) => !excludedIds.has(player.id));

    const normalizeActivision = (value) =>
      String(value || "")
        .split("#", 1)[0]
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const similarity = (a, b) => {
      if (!a.length && !b.length) return 1;
      if (!a.length || !b.length) return 0;

      const rows = a.length + 1;
      const cols = b.length + 1;
      const dp = Array.from({ length: rows }, () => Array(cols).fill(0));

      for (let i = 0; i < rows; i += 1) dp[i][0] = i;
      for (let j = 0; j < cols; j += 1) dp[0][j] = j;

      for (let i = 1; i < rows; i += 1) {
        for (let j = 1; j < cols; j += 1) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,
            dp[i][j - 1] + 1,
            dp[i - 1][j - 1] + cost
          );
        }
      }

      const distance = dp[a.length][b.length];
      return 1 - distance / Math.max(a.length, b.length);
    };

    const adjacency = new Map();

    candidates.forEach((player) => adjacency.set(player.id, new Set()));

    for (let i = 0; i < candidates.length; i += 1) {
      const left = normalizeActivision(candidates[i].activision_id);
      if (left.length < 3) continue;

      for (let j = i + 1; j < candidates.length; j += 1) {
        const right = normalizeActivision(candidates[j].activision_id);
        if (right.length < 3) continue;

        if (similarity(left, right) >= 0.8) {
          adjacency.get(candidates[i].id).add(candidates[j].id);
          adjacency.get(candidates[j].id).add(candidates[i].id);
        }
      }
    }

    const groupByPlayer = new Map();
    const visited = new Set();
    let groupIndex = 0;

    candidates.forEach((player) => {
      if (visited.has(player.id) || !adjacency.get(player.id)?.size) return;

      const stack = [player.id];
      const members = [];
      visited.add(player.id);

      while (stack.length) {
        const current = stack.pop();
        members.push(current);

        adjacency.get(current)?.forEach((next) => {
          if (!visited.has(next)) {
            visited.add(next);
            stack.push(next);
          }
        });
      }

      members.forEach((id) => groupByPlayer.set(id, groupIndex));
      groupIndex += 1;
    });

    return { groupByPlayer };
  }, [players, duplicateReview.excluded_player_ids]);

  const visiblePlayers = useMemo(() => {
    const query = searchActivision.trim().toLowerCase();

    const filtered = players.filter((player) => {
      const matchesSearch = !query || [
      player.activision_id,
      player.discord_username,
    ].some(value => String(value || "").toLowerCase().includes(query));

      const currentClanId = player.current_clan_id || null;
      const matchesClan =
        !clanFilter ||
        (clanFilter === "__none__"
          ? !currentClanId
          : currentClanId === clanFilter);

      return matchesSearch && matchesClan;
    });

    return [...filtered].sort((a, b) => {
      const groupA = duplicateInfo.groupByPlayer.get(a.id);
      const groupB = duplicateInfo.groupByPlayer.get(b.id);

      if (groupA !== undefined && groupB !== undefined && groupA !== groupB) return groupA - groupB;
      if (groupA !== undefined && groupB === undefined) return -1;
      if (groupA === undefined && groupB !== undefined) return 1;

      if (groupA !== undefined && groupB !== undefined && groupA === groupB) {
        return String(a.activision_id || "").localeCompare(
          String(b.activision_id || ""),
          undefined,
          { sensitivity: "base" },
        );
      }

      return Number(b.kd || 0) - Number(a.kd || 0);
    });
  }, [players, clanFilter, searchActivision, duplicateInfo]);

  const possibleDuplicatePlayers = useMemo(
    () => visiblePlayers.filter((player) => duplicateInfo.groupByPlayer.has(player.id)),
    [visiblePlayers, duplicateInfo],
  );

  const regularPlayers = useMemo(
    () => visiblePlayers.filter((player) => !duplicateInfo.groupByPlayer.has(player.id)),
    [visiblePlayers, duplicateInfo],
  );

  const openDuplicateMerge = (player) => {
    resetMerge();
    setMergeTargetSearch("");
    setMergeSourceSearch(player.activision_id || "");
    setMergeTargetClan("");
    setMergeSourceClan("");
    setEditingPlayer(null);
    setOpen(false);
    setMergeOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const excludePossibleDuplicate = async (player) => {
    if (!duplicateReview.tournament_id) {
      toast.error("Nessun ciclo di verifica attivo. Occorre inizializzare il torneo.");
      return;
    }

    setExcludingPlayerId(player.id);
    try {
      await api.post(`/players/${player.id}/duplicate-review/exclude`, {
        tournament_id: duplicateReview.tournament_id,
      });
      toast.success(`${playerLabel(player)} contrassegnato come non duplicato`);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setExcludingPlayerId(null);
    }
  };

  const create = async () => {
    try { await api.post("/players", form); toast.success("Giocatore creato"); setOpen(false); setForm(NEW); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try {
      await api.delete(`/players/${id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const openEditPlayer = (player) => {
    setEditingPlayer(player);
    setEditForm({
      discord_username: player.discord_username || "",
      discord_id: player.discord_id || "",
      activision_id: player.activision_id || "",
      kills: player.kills ?? 0,
      deaths: player.deaths ?? 0,
      wins: player.wins ?? 0,
    });
    setOpen(false);
    setMergeOpen(false);
    resetMerge();
  };

  const savePlayerEdit = async () => {
    const activisionId = editForm.activision_id.trim();
    if (!editingPlayer || !activisionId) {
      toast.error("Activision ID obbligatorio.");
      return;
    }

    setEditBusy(true);
    try {
      const discordId = String(editForm.discord_id || "").trim();
      if (discordId && !/^\d{15,22}$/.test(discordId)) {
        toast.error("Discord ID non valido: inserisci l'ID numerico completo.");
        return;
      }
      const discordIdChanged = discordId !== String(editingPlayer.discord_id || "").trim();
      await api.put(`/players/${editingPlayer.id}`, {
        activision_id: activisionId,
        discord_id: discordId || null,
        discord_username: discordIdChanged
          ? (editForm.discord_username.trim() === String(editingPlayer.discord_username || "").trim()
              ? ""
              : editForm.discord_username.trim())
          : editForm.discord_username.trim(),
        kills: Number(editForm.kills),
        deaths: Number(editForm.deaths),
        wins: Number(editForm.wins),
      });
      toast.success("Dati giocatore aggiornati");
      setEditingPlayer(null);
      setEditForm({ ...NEW });
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setEditBusy(false);
    }
  };

  const resetMerge = () => {
    setMergeTargetId("");
    setMergeSourceId("");
    setMergeClanHistoryChoice("auto");
    setMergeClanPeriods({});
    setMergeConfirmOpen(false);
  };

  const targetPlayer = players.find((p) => p.id === mergeTargetId);
  const sourcePlayer = players.find((p) => p.id === mergeSourceId);

  const mergeHistoryPeriods = [
    ...(targetPlayer?.clan_history || []).map((period) => ({
      ...period,
      owner: "Principale",
    })),
    ...(sourcePlayer?.clan_history || []).map((period) => ({
      ...period,
      owner: "Duplicato",
    })),
  ];

  const toLocalDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const fromLocalDateTime = (value) => (
    value ? new Date(value).toISOString() : null
  );

  const initializeMergeClanPeriods = () => {
    const initial = {};
    mergeHistoryPeriods.forEach((period) => {
      initial[period.id] = {
        selected: true,
        joined_at: toLocalDateTime(period.joined_at),
        left_at: toLocalDateTime(period.left_at),
      };
    });
    setMergeClanPeriods(initial);
  };

  const mergeCandidates = (search, clanId, excludedId, selectedId) => {
    const query = search.trim().toLowerCase();
    return players.filter((player) => {
      if (player.id === excludedId) return false;
      if (player.id === selectedId) return true;
      const matchesText = !query || [
        player.activision_id,
        player.discord_username,
      ].some((value) => String(value || "").toLowerCase().includes(query));
      const currentClanId = player.current_clan_id || null;
      const matchesClan = !clanId ||
        (clanId === "__none__"
          ? !currentClanId
          : currentClanId === clanId);
      return matchesText && matchesClan;
    });
  };

  const selectedMergeClanPeriods = () => (
    mergeHistoryPeriods
      .filter((period) => mergeClanPeriods[period.id]?.selected)
      .map((period) => {
        const edited = mergeClanPeriods[period.id];
        return {
          id: period.id,
          joined_at: fromLocalDateTime(edited.joined_at),
          left_at: fromLocalDateTime(edited.left_at),
        };
      })
  );

  const validateMergeClanHistory = () => {
    if (mergeClanHistoryChoice !== "manual") return true;

    const selected = selectedMergeClanPeriods();

    if (mergeHistoryPeriods.length && !selected.length) {
      toast.error("Seleziona almeno un periodo dello storico clan.");
      return false;
    }

    if (selected.some((period) => !period.id || !period.joined_at)) {
      toast.error("Controlla le date d'ingresso dei periodi selezionati.");
      return false;
    }

    for (let i = 0; i < selected.length; i += 1) {
      const first = selected[i];
      const start = new Date(first.joined_at).getTime();
      const end = first.left_at ? new Date(first.left_at).getTime() : Infinity;

      if (end <= start) {
        toast.error("La data d'uscita deve essere successiva all'ingresso.");
        return false;
      }

      for (let j = i + 1; j < selected.length; j += 1) {
        const second = selected[j];
        const otherStart = new Date(second.joined_at).getTime();
        const otherEnd = second.left_at
          ? new Date(second.left_at).getTime()
          : Infinity;

        if (start < otherEnd && otherStart < end) {
          toast.error("Due periodi clan si sovrappongono: correggi le date.");
          return false;
        }
      }
    }

    return true;
  };

  const requestMergeConfirmation = () => {
    if (!mergeTargetId || !mergeSourceId) {
      toast.error("Seleziona entrambi i giocatori.");
      return;
    }

    if (mergeTargetId === mergeSourceId) {
      toast.error("I due giocatori devono essere diversi.");
      return;
    }

    if (!validateMergeClanHistory()) return;

    setMergeConfirmOpen(true);
  };

  const mergePlayers = async () => {
    if (!targetPlayer || !sourcePlayer || mergeTargetId === mergeSourceId) {
      toast.error("Selezione giocatori non valida.");
      return;
    }

    if (!validateMergeClanHistory()) {
      setMergeConfirmOpen(false);
      return;
    }

    setMergeBusy(true);

    try {
      await api.post(`/players/${mergeTargetId}/merge`, {
        source_player_id: mergeSourceId,
        ...mergeChoices,
        clan_history_choice: mergeClanHistoryChoice,
        clan_history_periods: mergeClanHistoryChoice === "manual"
          ? selectedMergeClanPeriods()
          : [],
      });

      toast.success(
        `${playerLabel(sourcePlayer)} associato a ${playerLabel(targetPlayer)}`
      );

      setMergeOpen(false);
      resetMerge();
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setMergeBusy(false);
    }
  };

  const f = (k, label, type = "text") => (
    <div><label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">{label}</label>
      <input data-testid={`player-${k}`} type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: type === "number" ? +e.target.value : e.target.value })}
        className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" /></div>
  );

  return (
    <div>
      <PageTitle title="Giocatori" subtitle="Anagrafica & Statistiche"
        action={canEdit ? (
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="merge-player-btn"
              onClick={() => {
                setMergeOpen(!mergeOpen);
                setOpen(false);
                resetMerge();
              }}
              className="btn-outline-gold clip-beveled px-6 py-2.5 flex items-center gap-2"
            >
              <GitMerge className="w-4 h-4" /> Unisci Giocatori
            </button>
            <button
              data-testid="new-player-btn"
              onClick={() => {
                setOpen(!open);
                setMergeOpen(false);
                resetMerge();
              }}
              className="btn-gold clip-beveled px-6 py-2.5 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Nuovo Giocatore
            </button>
          </div>
        ) : null} />
      {canEdit && open && (
        <Panel gold className="p-6 mb-6 boot-in" testid="player-form">
          <div className="grid md:grid-cols-3 gap-4">
            {f("discord_username", "Discord Username")}{f("discord_id", "Discord ID")}{f("activision_id", "Activision ID")}
            {f("kills", "Kills", "number")}{f("deaths", "Deaths", "number")}{f("wins", "Wins", "number")}
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="save-player-btn" onClick={create} className="btn-gold clip-beveled px-6 py-2">Crea</button>
            <button onClick={() => setOpen(false)} className="btn-outline-gold clip-beveled px-6 py-2">Annulla</button>
          </div>
        </Panel>
      )}
      {canEdit && editingPlayer && (
        <Panel gold className="p-6 mb-6 boot-in" testid="player-edit-form">
          <div className="mb-4">
            <h2 className="font-teko text-2xl uppercase tracking-wide text-[var(--gold)]">
              Modifica Giocatore
            </h2>
            <p className="font-rajdhani text-sm text-[var(--silver)]">
              {editingPlayer.discord_username || editingPlayer.discord_id || "Giocatore"}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              ["discord_username", "Discord Username", "text"],
              ["discord_id", "Discord ID numerico", "text"],
              ["activision_id", "Activision ID", "text"],
              ["kills", "Kills", "number"],
              ["deaths", "Deaths", "number"],
              ["wins", "Wins", "number"],
            ].map(([key, label, type]) => (
              <div key={key}>
                <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
                  {label}
                </label>
                <input
                  data-testid={`edit-player-${key.replaceAll("_", "-")}`}
                  type={type}
                  min={type === "number" ? 0 : undefined}
                  value={editForm[key]}
                  onChange={(e) => setEditForm((previous) => ({
                    ...previous,
                    [key]: type === "number" ? Number(e.target.value) : e.target.value,
                  }))}
                  className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
                />
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm font-rajdhani text-[var(--silver)]">
            Il K/D viene ricalcolato automaticamente. Se cambi Discord ID, verifica anche lo username:
            il vecchio nome non verrà conservato automaticamente.
          </p>

          <div className="flex gap-3 mt-4">
            <button
              data-testid="save-player-edit-btn"
              onClick={savePlayerEdit}
              disabled={editBusy}
              className="btn-gold clip-beveled px-6 py-2 disabled:opacity-50"
            >
              {editBusy ? "Salvataggio..." : "Salva Modifica"}
            </button>
            <button
              onClick={() => {
                setEditingPlayer(null);
                setEditForm({ ...NEW });
              }}
              disabled={editBusy}
              className="btn-outline-gold clip-beveled px-6 py-2 disabled:opacity-50"
            >
              Annulla
            </button>
          </div>
        </Panel>
      )}

      {canEdit && mergeOpen && (
        <Panel gold className="p-6 mb-6 boot-in" testid="player-merge-form">
          <div className="mb-5">
            <h2 className="font-teko text-2xl uppercase tracking-wide text-[var(--gold)]">
              Unisci giocatori
            </h2>
            <p className="font-rajdhani text-sm text-[var(--silver)] max-w-3xl">
              Seleziona l'identità che deve rimanere e il duplicato da associare.
              Squadre e risultati storici non vengono riscritti.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            <div>
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--gold)]">
                1. Giocatore da mantenere
              </label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2 mb-2">
                <input
                  type="search"
                  data-testid="merge-target-search"
                  placeholder="Cerca Activision ID o nickname"
                  value={mergeTargetSearch}
                  onChange={(e) => setMergeTargetSearch(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
                />
                <select
                  data-testid="merge-target-clan"
                  value={mergeTargetClan}
                  onChange={(e) => setMergeTargetClan(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
                >
                  <option value="">Tutti i clan</option>
                  <option value="__none__">Senza clan</option>
                  {clans.map((clan) => (
                    <option key={clan.id} value={clan.id}>{clan.name}</option>
                  ))}
                </select>
              </div>
              <select
                data-testid="merge-target-player"
                value={mergeTargetId}
                onChange={(e) => {
                  setMergeTargetId(e.target.value);
                  setMergeClanHistoryChoice("auto");
                  setMergeClanPeriods({});
                  setMergeConfirmOpen(false);
                }}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--gold)]/50 clip-beveled-sm px-3 py-2.5 text-white focus:border-[var(--gold)] focus:outline-none"
              >
                <option value="">Seleziona il giocatore principale</option>
                {mergeCandidates(mergeTargetSearch, mergeTargetClan, mergeSourceId, mergeTargetId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {playerLabel(p)}
                      {p.discord_username ? ` — ${p.discord_username}` : ""}
                    </option>
                  ))}
              </select>

              {targetPlayer && (
                <div className="mt-3 border border-[var(--gold)]/40 bg-[#0a0a0a] p-4 font-rajdhani">
                  <div className="font-teko text-lg uppercase text-[var(--gold)]">
                    Identità che rimane
                  </div>
                  <div className="text-white font-semibold">
                    {targetPlayer.discord_username || "Discord non indicato"}
                  </div>
                  <div className="text-[var(--silver)]">
                    Activision: {targetPlayer.activision_id || "—"}
                  </div>
                  <div className="mt-2 text-sm text-[var(--silver)]">
                    Kills {targetPlayer.kills ?? 0} · Deaths {targetPlayer.deaths ?? 0} ·
                    K/D {targetPlayer.kd ?? 0} · Wins {targetPlayer.wins ?? 0}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--destructive)]">
                2. Duplicato da assorbire
              </label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2 mb-2">
                <input
                  type="search"
                  data-testid="merge-source-search"
                  placeholder="Cerca Activision ID o nickname"
                  value={mergeSourceSearch}
                  onChange={(e) => setMergeSourceSearch(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
                />
                <select
                  data-testid="merge-source-clan"
                  value={mergeSourceClan}
                  onChange={(e) => setMergeSourceClan(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
                >
                  <option value="">Tutti i clan</option>
                  <option value="__none__">Senza clan</option>
                  {clans.map((clan) => (
                    <option key={clan.id} value={clan.id}>{clan.name}</option>
                  ))}
                </select>
              </div>
              <select
                data-testid="merge-source-player"
                value={mergeSourceId}
                onChange={(e) => {
                  setMergeSourceId(e.target.value);
                  setMergeClanHistoryChoice("auto");
                  setMergeClanPeriods({});
                  setMergeConfirmOpen(false);
                }}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--destructive)]/50 clip-beveled-sm px-3 py-2.5 text-white focus:border-[var(--destructive)] focus:outline-none"
              >
                <option value="">Seleziona il duplicato</option>
                {mergeCandidates(mergeSourceSearch, mergeSourceClan, mergeTargetId, mergeSourceId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {playerLabel(p)}
                      {p.discord_username ? ` — ${p.discord_username}` : ""}
                    </option>
                  ))}
              </select>

              {sourcePlayer && (
                <div className="mt-3 border border-[var(--destructive)]/50 bg-[#0a0a0a] p-4 font-rajdhani">
                  <div className="font-teko text-lg uppercase text-[var(--destructive)]">
                    Identità duplicata
                  </div>
                  <div className="text-white font-semibold">
                    {sourcePlayer.discord_username || "Discord non indicato"}
                  </div>
                  <div className="text-[var(--silver)]">
                    Activision: {sourcePlayer.activision_id || "—"}
                  </div>
                  <div className="mt-2 text-sm text-[var(--silver)]">
                    Kills {sourcePlayer.kills ?? 0} · Deaths {sourcePlayer.deaths ?? 0} ·
                    K/D {sourcePlayer.kd ?? 0} · Wins {sourcePlayer.wins ?? 0}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 border border-[var(--gold)]/40 bg-[#0a0a0a] p-4">
            <h3 className="font-teko text-xl uppercase text-[var(--gold)]">
              Scegli quali dati conservare
            </h3>
            <p className="font-rajdhani text-sm text-[var(--silver)] mb-4">
              Ogni campo può essere scelto indipendentemente.
              Il K/D statistico viene ricalcolato automaticamente.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                ["activision_id_choice", "Activision ID", "activision_id", false, false],
                ["discord_id_choice", "Discord ID", "discord_id", false, false],
                ["discord_username_choice", "Username Discord", "discord_username", false, false],
                ["avatar_choice", "Avatar", "avatar", false, false],
                ["kills_choice", "Kills", "kills", true, false],
                ["deaths_choice", "Deaths", "deaths", true, false],
                ["wins_choice", "Wins", "wins", true, false],
                ["battle_royale_kd_choice", "K/D verificato Battle Royale", "battle_royale", false, true],
                ["resurgence_kd_choice", "K/D verificato Resurgence", "resurgence", false, true],
              ].map(([key, label, field, allowSum, verified]) => {
                const displayValue = (player) => {
                  if (!player) return "—";
                  const value = verified
                    ? player.verified_kd?.[field]
                    : player[field];
                  if (value == null || value === "") return "—";
                  if (verified && typeof value === "object") {
                    return String(value.kd ?? value.value ?? "Verifica presente");
                  }
                  return String(value);
                };
                return (
                  <div key={key}>
                    <label className="block font-rajdhani text-sm text-white mb-1">
                      {label}
                    </label>
                    <select
                      data-testid={`merge-choice-${key}`}
                      value={mergeChoices[key]}
                      onChange={(e) => {
                        setMergeChoices((previous) => ({
                          ...previous,
                          [key]: e.target.value,
                        }));
                        setMergeConfirmOpen(false);
                      }}
                      className="w-full bg-[#111] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
                    >
                      {verified && <option value="auto">Automatico</option>}
                      <option value="target">
                        {`Principale: ${displayValue(targetPlayer)}`}
                      </option>
                      <option value="source">
                        {`Duplicato: ${displayValue(sourcePlayer)}`}
                      </option>
                      {allowSum && <option value="sum">Somma dei due</option>}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-5 border border-[var(--gold)]/40 bg-[#0a0a0a] p-4">
            <h3 className="font-teko text-xl uppercase text-[var(--gold)]">
              Storico clan
            </h3>
            <p className="font-rajdhani text-sm text-[var(--silver)] mb-3">
              Scegli come gestire i periodi di appartenenza ai clan.
            </p>

            <select
              data-testid="merge-clan-history-choice"
              value={mergeClanHistoryChoice}
              onChange={(e) => {
                const choice = e.target.value;
                setMergeClanHistoryChoice(choice);
                if (choice === "manual") initializeMergeClanPeriods();
                setMergeConfirmOpen(false);
              }}
              className="w-full bg-[#111] border border-[var(--steel)]/50 px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
            >
              <option value="auto">Automatico — consolida i periodi compatibili</option>
              <option value="manual">Manuale — scegli i periodi e modifica le date</option>
            </select>

            {mergeClanHistoryChoice === "manual" && (
              <div className="mt-4 space-y-3">
                {mergeHistoryPeriods.length === 0 && (
                  <p className="font-rajdhani text-sm text-[var(--silver)]">
                    Nessun periodo storico disponibile.
                  </p>
                )}

                {mergeHistoryPeriods.map((period) => {
                  const item = mergeClanPeriods[period.id] || {};
                  return (
                    <div
                      key={period.id}
                      className="border border-[var(--steel)]/40 bg-[#111] p-3"
                    >
                      <label className="flex items-center gap-2 font-rajdhani text-white">
                        <input
                          type="checkbox"
                          checked={Boolean(item.selected)}
                          onChange={(e) => {
                            setMergeClanPeriods((previous) => ({
                              ...previous,
                              [period.id]: {
                                ...previous[period.id],
                                selected: e.target.checked,
                              },
                            }));
                            setMergeConfirmOpen(false);
                          }}
                        />
                        <span>
                          {period.owner} — {clanById[period.clan_id]?.name || period.clan_id}
                        </span>
                      </label>

                      {item.selected && (
                        <div className="grid sm:grid-cols-2 gap-3 mt-3">
                          {[
                            ["joined_at", "Data ingresso"],
                            ["left_at", "Data uscita (vuota = attivo)"],
                          ].map(([field, label]) => (
                            <label key={field} className="font-rajdhani text-sm text-[var(--silver)]">
                              {label}
                              <input
                                type="datetime-local"
                                value={item[field] || ""}
                                onChange={(e) => {
                                  setMergeClanPeriods((previous) => ({
                                    ...previous,
                                    [period.id]: {
                                      ...previous[period.id],
                                      [field]: e.target.value,
                                    },
                                  }));
                                  setMergeConfirmOpen(false);
                                }}
                                className="block w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 px-3 py-2 text-white"
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-5 border border-[var(--steel)]/40 bg-[#0a0a0a] p-4 font-rajdhani text-sm text-[var(--silver)]">
            <span className="text-white font-semibold">Cosa succede:</span>{" "}
            kills, deaths e vittorie memorizzate vengono sommate sul giocatore
            principale; statistiche storiche e MVP vengono ricondotte alla stessa
            identità; i K/D VERIFIED vengono conservati per modalità.
          </div>

          {!mergeConfirmOpen ? (
            <div className="flex flex-wrap gap-3 mt-5">
              <button
                data-testid="merge-review-btn"
                onClick={requestMergeConfirmation}
                disabled={!mergeTargetId || !mergeSourceId || mergeTargetId === mergeSourceId}
                className="btn-gold clip-beveled px-6 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continua
              </button>
              <button
                onClick={() => {
                  setMergeOpen(false);
                  resetMerge();
                }}
                className="btn-outline-gold clip-beveled px-6 py-2"
              >
                Annulla
              </button>
            </div>
          ) : (
            <div
              data-testid="merge-confirmation"
              className="mt-5 border border-[var(--destructive)]/60 bg-[var(--destructive)]/5 p-5"
            >
              <div className="font-teko text-xl uppercase tracking-wide text-[var(--destructive)]">
                Conferma associazione
              </div>
              <p className="font-rajdhani text-white mt-1">
                Stai associando <strong>{playerLabel(sourcePlayer)}</strong> a{" "}
                <strong>{playerLabel(targetPlayer)}</strong>.
              </p>
              <p className="font-rajdhani text-sm text-[var(--silver)] mt-2">
                Il duplicato non comparirà più tra i giocatori attivi.
              </p>

              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  data-testid="merge-confirm-btn"
                  onClick={mergePlayers}
                  disabled={mergeBusy}
                  className="bg-[var(--destructive)] text-white font-rajdhani font-bold uppercase tracking-wide clip-beveled px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mergeBusy ? "Associazione in corso..." : "Conferma e unisci"}
                </button>
                <button
                  onClick={() => setMergeConfirmOpen(false)}
                  disabled={mergeBusy}
                  className="btn-outline-gold clip-beveled px-6 py-2 disabled:opacity-50"
                >
                  Torna indietro
                </button>
              </div>
            </div>
          )}
        </Panel>
      )}

      <Panel className="p-4 mb-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
              Cerca Activision ID
            </label>
            <input
              data-testid="player-activision-search"
              value={searchActivision}
              onChange={(e) => setSearchActivision(e.target.value)}
              placeholder="Es. Ciry91"
              className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
            />
          </div>

          <div>
            <label className="font-rajdhani text-[10px] uppercase tracking-widest text-[var(--silver)]">
              Filtra per Clan
            </label>
            <select
              data-testid="player-clan-filter"
              value={clanFilter}
              onChange={(e) => setClanFilter(e.target.value)}
              className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
            >
              <option value="">Tutti i clan</option>
              <option value="__none__">Senza clan</option>
              {clans.map((clan) => (
                <option key={clan.id} value={clan.id}>
                  {clan.name} [{clan.tag}]
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 font-rajdhani text-sm text-[var(--silver)]">
          <span>{visiblePlayers.length} giocatori visualizzati</span>
          <span className="flex items-center gap-2">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400" />
            Possibile duplicato Activision ID
          </span>
        </div>
      </Panel>

      {historyPlayerId && (() => {
        const player = players.find((p) => p.id === historyPlayerId);
        if (!player) return null;

        const history = player.clan_history || [];
        const formatDate = (value) => {
          if (!value) return "Attuale";
          const date = new Date(value);
          return Number.isNaN(date.getTime())
            ? "Data non disponibile"
            : date.toLocaleDateString("it-IT", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              });
        };

        return (
          <Panel gold className="p-6 mb-4 boot-in" testid="player-clan-history">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="font-teko text-2xl uppercase tracking-wide text-[var(--gold)]">
                  Storico clan
                </h2>
                <p className="font-rajdhani text-[var(--silver)]">
                  {playerLabel(player)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setHistoryPlayerId(null)}
                className="btn-outline-gold clip-beveled px-4 py-2"
              >
                Chiudi
              </button>
            </div>
            <div className="space-y-3">
              {history.map((period, index) => {
                const clan = clanById[period.clan_id];
                const current = period.left_at == null;

                return (
                  <div
                    key={`${period.clan_id}-${period.joined_at}-${index}`}
                    className="border border-[var(--steel)]/40 bg-[#0a0a0a] p-3 font-rajdhani"
                  >
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="text-white font-semibold">
                        {clan
                          ? `${clan.name} [${clan.tag}]`
                          : period.clan_id}
                      </span>
                      <span className="text-[var(--gold)]">
                        {current ? "Clan attuale" : "Clan precedente"}
                      </span>
                    </div>
                    <div className="text-sm text-[var(--silver)] mt-1">
                      Dal {formatDate(period.joined_at)}
                      {" — "}
                      {current
                        ? "Attualmente nel clan"
                        : `al ${formatDate(period.left_at)}`}
                    </div>
                    {period.date_confidence === "inferred" && (
                      <div className="text-xs text-[var(--silver)] mt-1">
                        Date stimate dalle iscrizioni alle squadre
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Panel>
        );
      })()}

      <Panel className="overflow-x-auto mb-6" testid="possible-duplicates-table">
        <div className="px-4 pt-4 pb-3">
          <h2 className="font-teko text-2xl uppercase tracking-wide text-[var(--gold)]">
            Possibili duplicati ({possibleDuplicatePlayers.length})
          </h2>
          <p className="font-rajdhani text-sm text-[var(--silver)]">
            Verifica ogni Activision ID sospetto. Puoi unire i profili oppure
            escludere singolarmente gli ID che non sono duplicati.
          </p>
          {duplicateReview.tournament_name && (
            <p className="font-rajdhani text-xs text-[var(--silver)] mt-1">
              Ciclo di verifica: {duplicateReview.tournament_name}
            </p>
          )}
        </div>
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/40 font-teko text-lg text-[var(--gold-light)] tracking-wide">
              <th className="text-left px-4 py-3">Gruppo</th>
              <th className="text-left px-4 py-3">Giocatore</th>
              <th className="text-left px-4 py-3">Activision ID</th>
              <th className="text-left px-4 py-3">Clan</th>
              <th className="text-right px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {possibleDuplicatePlayers.map((p) => {
              const currentClan = clanById[p.current_clan_id];
              const groupNumber = duplicateInfo.groupByPlayer.get(p.id) + 1;
              return (
                <tr key={p.id} className="border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani">
                  <td className="px-4 py-3 text-[var(--gold)] font-semibold">
                    #{groupNumber}
                  </td>
                  <td className="px-4 py-3 text-white">{p.discord_username || "—"}</td>
                  <td className="px-4 py-3 text-[var(--silver)]">{p.activision_id}</td>
                  <td className="px-4 py-3 text-[var(--silver)]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{currentClan ? `${currentClan.name} [${currentClan.tag}]` : "—"}</span>
                      {(p.clan_history || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => setHistoryPlayerId(p.id)}
                          className="text-[var(--gold)] underline underline-offset-2 text-sm"
                          aria-label={`Storico clan di ${playerLabel(p)}`}
                        >
                          Storico ({p.clan_history.length})
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {canEdit && (
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openDuplicateMerge(p)}
                          className="btn-outline-gold clip-beveled px-3 py-1.5 text-sm"
                        >
                          Unisci
                        </button>
                        <button
                          type="button"
                          onClick={() => excludePossibleDuplicate(p)}
                          disabled={excludingPlayerId === p.id || !duplicateReview.tournament_id}
                          className="btn-outline-gold clip-beveled px-3 py-1.5 text-sm disabled:opacity-40"
                        >
                          {excludingPlayerId === p.id ? "Salvataggio..." : "Non è duplicato"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {possibleDuplicatePlayers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center font-rajdhani text-[var(--silver)]">
                  Nessun possibile duplicato da verificare.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Panel>

      <div className="mb-3">
        <h2 className="font-teko text-2xl uppercase tracking-wide text-[var(--gold)]">
          Elenco giocatori ({regularPlayers.length})
        </h2>
      </div>
      <Panel className="overflow-x-auto" testid="players-table">
        <table className="w-full min-w-[640px]">
          <thead><tr className="bg-[#0a0a0a] border-b border-[var(--gold)]/40 font-teko text-lg text-[var(--gold-light)] tracking-wide">
            <th className="text-left px-4 py-3">Giocatore</th><th className="text-left px-4 py-3">Activision ID</th>
            <th className="text-left px-4 py-3">Clan</th>
            <th className="text-right px-4 py-3">K/D</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {regularPlayers.map((p) => {
              const currentClan = clanById[p.current_clan_id];
              const historyCount = (p.clan_history || []).length;

              return (
              <tr key={p.id} className="border-b border-[var(--steel)]/20 hover:bg-[#1a1a1a] font-rajdhani" data-testid={`player-row-${p.id}`}>
                <td className="px-4 py-3 text-white">{p.discord_username}</td>
                <td className="px-4 py-3 text-[var(--silver)]">
                  <div className="flex items-center gap-2">
                    <span>{p.activision_id}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-[var(--silver)]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span>
                      {currentClan
                        ? `${currentClan.name} [${currentClan.tag}]`
                        : "—"}
                    </span>
                    {historyCount > 1 && (
                      <button
                        type="button"
                        onClick={() => setHistoryPlayerId(p.id)}
                        className="text-[var(--gold)] underline underline-offset-2 text-sm"
                        aria-label={`Storico clan di ${playerLabel(p)}`}
                      >
                        Storico ({historyCount})
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-oswald font-bold text-[var(--gold)]">{p.kd}</td>
                <td className="px-4 py-3 text-right">
                  {canEdit && (
                    <div className="flex justify-end gap-3">
                      <button
                        data-testid={`edit-player-${p.id}`}
                        onClick={() => openEditPlayer(p)}
                        className="text-[var(--gold)]"
                        aria-label={`Modifica ${playerLabel(p)}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => remove(p.id)}
                        className="text-[var(--destructive)]"
                        aria-label={`Elimina ${playerLabel(p)}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
